import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { MobileLogListResponse, MobileLogRecord } from './mobile-log.interface';

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

interface CompaniesResponse { items?: CompaniesRecord[]; data?: CompaniesRecord[]; results?: CompaniesRecord[]; companiesRecords?: CompaniesRecord[]; }

@Component({
  selector: 'app-mobile-log',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './mobile-log.component.html',
  styleUrl: './mobile-log.component.scss',
})
export class MobileLogComponent implements OnInit {
  dataSource = new MatTableDataSource<MobileLogRecord>([]);
  employees: EmployeeRecord[] = [];
  companies: CompaniesRecord[] = [];
  isLoading = true;
  actions: TableAction<MobileLogRecord>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<MobileLogRecord>[] = [];
  activeFilters: Record<string, string | number | boolean | null> = {};
  currentPage = 1;
  pageSize = 15;
  totalItems = 0;
  private loadRequestSequence = 0;
  searchValue = '';

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  readonly entityTypeOptions = ['ATTENDANCE'] as const;
  readonly eventTypeOptions = ['PUNCH'] as const;
  readonly sourceOptions = ['USER', 'SYSTEM'] as const;
  readonly statusOptions = ['SUCCEEDED', 'FAILED', 'PENDING'] as const;

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.tableFilters = this.buildTableFilters();
      this.dataSource.data = this.dataSource.data.map((item) => this.normalizeMobileLog(item));
    });
    void this.initializeData();
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadCompanies();
      await this.loadEmployees();
      this.tableFilters = this.buildTableFilters();
      await this.loadData();
    } finally {
      this.isLoading = false;
    }
  }

  async loadData(): Promise<void> {
    const requestId = ++this.loadRequestSequence;
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<MobileLogListResponse>('mobile/logs', {
          params: {
            page: this.currentPage,
            perPage: this.pageSize,
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...(this.searchValue ? { recordId: this.searchValue } : {}),
            ...(this.getActiveEmployeeId() ? { employeeId: this.getActiveEmployeeId() } : {}),
            ...(this.getActiveEntityType() ? { entityType: this.getActiveEntityType() } : {}),
            ...(this.getActiveEventType() ? { eventType: this.getActiveEventType() } : {}),
            ...(this.getActiveSource() ? { source: this.getActiveSource() } : {}),
            ...(this.getActiveStatus() ? { status: this.getActiveStatus() } : {}),
            ...(this.getActiveDateFrom() ? { dateFrom: this.toDayStartIso(this.getActiveDateFrom()) } : {}),
            ...(this.getActiveDateTo() ? { dateTo: this.toDayEndIso(this.getActiveDateTo()) } : {}),
          },
        })
      );

      if (requestId !== this.loadRequestSequence) return;

      this.totalItems = response?.pagination?.total || 0;
      this.dataSource.data = (response?.data || []).map((item) => this.normalizeMobileLog(item));
      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      if (requestId !== this.loadRequestSequence) return;
      console.error('Error loading mobile logs', error);
      this.dataSource.data = [];
      this.totalItems = 0;
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('mobileLogsCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      if (requestId === this.loadRequestSequence) this.isLoading = false;
    }
  }

  async handleView(row: MobileLogRecord): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/mobile-logs', row.mobileLogId], { queryParamsHandling: 'preserve' });
  }

  onSearchChange(search: string): void {
    this.searchValue = search.trim();
    this.currentPage = 1;
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      void this.loadData();
    }, 300);
  }

  async onFiltersChange(filters: Record<string, string | number | boolean | null>): Promise<void> {
    const { reasonCode: _reasonCode, administrativeStatus: _administrativeStatus, ...visibleFilters } = filters;
    const previousCompanyId = this.getActiveCompanyId();
    this.activeFilters = visibleFilters;
    this.currentPage = 1;
    this.tableFilters = this.buildTableFilters();
    if (previousCompanyId !== this.getActiveCompanyId()) {
      this.activeFilters['employee'] = null;
      await this.loadEmployees();
    }
    await this.loadData();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    void this.loadData();
  }

  onFilterSearchChange(event: { filterId: string; search: string }): void {
    if (event.filterId !== 'employee') {
      return;
    }

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(event.search);
    }, 250);
  }

  private async loadEmployees(search = ''): Promise<void> {
    try {
      const selectedEmployeeId = this.getActiveEmployeeId();
      const selectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', {
          params: { page: 1, perPage: 20, isActive: 'true', ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}), ...(search ? { search } : {}) },
        })
      );
      const nextEmployees = this.extractEmployees(response)
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));

      this.employees = selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
        ? [selectedEmployee, ...nextEmployees]
        : nextEmployees;

      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      console.error('Error loading employees for mobile logs', error);
      this.employees = [];
    }
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private async loadCompanies(): Promise<void> {
    const response = await firstValueFrom(this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', { params: { isActive: 'true' } }));
    this.companies = (Array.isArray(response) ? response : response.items || response.data || response.results || response.companiesRecords || [])
      .filter((company) => company.isActive !== false)
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  private normalizeMobileLog(item: Partial<MobileLogRecord>): MobileLogRecord {
    const employee = this.employees.find((record) => record.employeeId === item.employeeId);
    const entityType = this.normalizeText(item.entityType) || '-';
    const eventType = this.normalizeText(item.eventType) || '-';
    const source = this.normalizeText(item.source) || '-';
    const status = this.normalizeText(item.status) || '-';
    const reasonCode = this.normalizeText(item.reasonCode) || '-';
    const isActive = item.isActive !== false;
    const lat = item.location?.lat ?? null;
    const lon = item.location?.lon ?? null;
    const acc = item.location?.acc ?? null;

    return {
      mobileLogId:
        item.mobileLogId
        || `${item.uid || 'mobile-log'}-${item.deviceCreatedAt || item.createdAt || 'row'}`,
      center: item.center || '',
      uid: item.uid || '-',
      userId: item.userId || null,
      employeeId: item.employeeId || null,
      employeeNumber: item.employeeNumber || employee?.employeeNumber || '-',
      employeeName: item.employeeName || employee?.fullName || null,
      companiesRecordId: item.companiesRecordId || null,
      orgUnitId: item.orgUnitId || null,
      orgPath: item.orgPath || null,
      entityType,
      eventType,
      source,
      actionType: item.actionType || null,
      status,
      reasonCode,
      description: item.description || null,
      deviceCreatedAt: item.deviceCreatedAt || null,
      location: item.location || null,
      metadata: item.metadata || null,
      isActive,
      createdAt: item.createdAt || null,
      updatedAt: item.updatedAt || null,
      employeeLabel: item.employeeName || employee?.fullName || item.employeeNumber || item.employeeId || '-',
      entityTypeLabel: this.translateOption('mobileLogsCatalog.entityTypes', entityType),
      eventTypeLabel: this.translateOption('mobileLogsCatalog.eventTypes', eventType),
      sourceLabel: this.translateOption('mobileLogsCatalog.sources', source),
      statusLabel: this.translateOption('mobileLogsCatalog.statuses', status),
      reasonCodeLabel: this.translateOption('mobileLogsCatalog.reasonCodes', reasonCode),
      deviceCreatedAtLabel: this.formatDateTime(item.deviceCreatedAt),
      createdAtLabel: this.formatDateTime(item.createdAt),
      updatedAtLabel: this.formatDateTime(item.updatedAt),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
      coordinatesLabel: lat !== null && lon !== null ? `${lat.toFixed(6)}, ${lon.toFixed(6)}` : '-',
      accuracyLabel: acc !== null && acc !== undefined ? `${acc} m` : '-',
    };
  }

  private buildTableFilters(): TableFilterDefinition<MobileLogRecord>[] {
    return [
      {
        id: 'company', label: this.t('orgUnitsCatalog.fields.company'), icon: 'business', searchable: true,
        value: this.activeFilters['company'] ?? null,
        options: this.companies.map((company) => ({ value: company.companiesRecordId, label: company.name, searchText: `${company.code || ''} ${company.center || ''}`.trim() })),
      },
      {
        id: 'employee',
        label: this.t('mobileLogsCatalog.filters.employee'),
        placeholder: this.t('mobileLogsCatalog.filters.employeePlaceholder'),
        icon: 'badge',
        searchable: true,
        value: this.activeFilters['employee'] ?? null,
        options: this.employees.map((employee) => ({
          value: employee.employeeId,
          label: `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}`,
          searchText: `${employee.fullName || ''} ${employee.employeeNumber || ''}`.trim(),
        })),
      },
      {
        id: 'entityType',
        label: this.t('mobileLogsCatalog.filters.entityType'),
        placeholder: this.t('mobileLogsCatalog.filters.entityTypePlaceholder'),
        icon: 'category',
        searchable: false,
        value: this.activeFilters['entityType'] ?? null,
        options: this.entityTypeOptions.map((value) => ({ value, label: this.t(`mobileLogsCatalog.entityTypes.${value}`) })),
      },
      {
        id: 'eventType',
        label: this.t('mobileLogsCatalog.filters.eventType'),
        placeholder: this.t('mobileLogsCatalog.filters.eventTypePlaceholder'),
        icon: 'touch_app',
        searchable: false,
        value: this.activeFilters['eventType'] ?? null,
        options: this.eventTypeOptions.map((value) => ({ value, label: this.t(`mobileLogsCatalog.eventTypes.${value}`) })),
      },
      {
        id: 'source',
        label: this.t('mobileLogsCatalog.filters.source'),
        placeholder: this.t('mobileLogsCatalog.filters.sourcePlaceholder'),
        icon: 'smartphone',
        searchable: false,
        value: this.activeFilters['source'] ?? null,
        options: this.sourceOptions.map((value) => ({ value, label: this.t(`mobileLogsCatalog.sources.${value}`) })),
      },
      {
        id: 'status',
        label: this.t('mobileLogsCatalog.filters.status'),
        placeholder: this.t('mobileLogsCatalog.filters.statusPlaceholder'),
        icon: 'task_alt',
        searchable: false,
        value: this.activeFilters['status'] ?? null,
        options: this.statusOptions.map((value) => ({ value, label: this.t(`mobileLogsCatalog.statuses.${value}`) })),
      },
      {
        id: 'dateFrom',
        label: this.t('mobileLogsCatalog.filters.dateFrom'),
        placeholder: this.t('mobileLogsCatalog.filters.dateFromPlaceholder'),
        icon: 'calendar_today',
        type: 'date',
        value: this.activeFilters['dateFrom'] ?? null,
      },
      {
        id: 'dateTo',
        label: this.t('mobileLogsCatalog.filters.dateTo'),
        placeholder: this.t('mobileLogsCatalog.filters.dateToPlaceholder'),
        icon: 'event',
        type: 'date',
        value: this.activeFilters['dateTo'] ?? null,
      },
    ];
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('mobileLogsCatalog.actions.view'),
        colorClass: 'info',
        auth: { resource: 'mobileLogs', action: 'read' },
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'employeeNumber', label: this.t('mobileLogsCatalog.fields.employeeNumber'), type: 'text', visible: true },
      { def: 'employeeLabel', label: this.t('mobileLogsCatalog.fields.employee'), type: 'text', visible: true },
      { def: 'uid', label: this.t('mobileLogsCatalog.fields.uid'), type: 'text', visible: true },
      { def: 'entityTypeLabel', label: this.t('mobileLogsCatalog.fields.entityType'), type: 'text', visible: true },
      { def: 'eventTypeLabel', label: this.t('mobileLogsCatalog.fields.eventType'), type: 'text', visible: true },
      { def: 'sourceLabel', label: this.t('mobileLogsCatalog.fields.source'), type: 'text', visible: true },
      { def: 'statusLabel', label: this.t('mobileLogsCatalog.fields.status'), type: 'status', visible: true },
      { def: 'reasonCodeLabel', label: this.t('mobileLogsCatalog.fields.reasonCode'), type: 'text', visible: true },
      { def: 'deviceCreatedAtLabel', label: this.t('mobileLogsCatalog.fields.deviceCreatedAt'), type: 'text', visible: true },
    ];

    this.tableFilters = this.buildTableFilters();
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private toDayStartIso(value: string): string {
    return new Date(`${value}T00:00:00`).toISOString();
  }

  private toDayEndIso(value: string): string {
    return new Date(`${value}T23:59:59.999`).toISOString();
  }

  private getActiveEmployeeId(): string {
    return (this.activeFilters['employee'] as string) || '';
  }

  private getActiveCompanyId(): string { return (this.activeFilters['company'] as string) || ''; }

  private getActiveEntityType(): string {
    return (this.activeFilters['entityType'] as string) || '';
  }

  private getActiveEventType(): string {
    return (this.activeFilters['eventType'] as string) || '';
  }

  private getActiveSource(): string {
    return (this.activeFilters['source'] as string) || '';
  }

  private getActiveStatus(): string {
    return (this.activeFilters['status'] as string) || '';
  }

  private getActiveDateFrom(): string {
    return (this.activeFilters['dateFrom'] as string) || '';
  }

  private getActiveDateTo(): string {
    return (this.activeFilters['dateTo'] as string) || '';
  }

  private normalizeText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private translateOption(baseKey: string, value: string): string {
    if (!value || value === '-') {
      return '-';
    }

    const key = `${baseKey}.${value}`;
    const translated = this.t(key);
    return translated !== key ? translated : value;
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'mobileLogs', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2200,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
